import React from 'react';
import { Chip, Tooltip } from '@mui/joy';
import { FileText, Clock, CheckCircle, XCircle as AlertCircle, XCircle } from '@phosphor-icons/react';

interface EagleViewStatusChipProps {
  status?: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | null;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showTooltip?: boolean;
}

export const EagleViewStatusChip: React.FC<EagleViewStatusChipProps> = ({
  status,
  size = 'sm',
  showIcon = true,
  showTooltip = true
}) => {
  if (!status) return null;

  const getStatusConfig = () => {
    switch (status) {
      case 'pending':
        return {
          color: 'warning' as const,
          icon: <Clock size={14} />,
          label: 'EV Pending',
          tooltip: 'EagleView order is pending processing'
        };
      case 'processing':
        return {
          color: 'primary' as const,
          icon: <FileText size={14} />,
          label: 'EV Processing',
          tooltip: 'EagleView report is being generated'
        };
      case 'completed':
        return {
          color: 'success' as const,
          icon: <CheckCircle size={14} />,
          label: 'EV Ready',
          tooltip: 'EagleView report is available'
        };
      case 'failed':
        return {
          color: 'danger' as const,
          icon: <AlertCircle size={14} />,
          label: 'EV Failed',
          tooltip: 'EagleView order failed'
        };
      case 'cancelled':
        return {
          color: 'neutral' as const,
          icon: <XCircle size={14} />,
          label: 'EV Cancelled',
          tooltip: 'EagleView order was cancelled'
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  const chip = (
    <Chip
      size={size}
      color={config.color}
      variant="soft"
      startDecorator={showIcon ? config.icon : undefined}
    >
      {config.label}
    </Chip>
  );

  if (showTooltip) {
    return (
      <Tooltip title={config.tooltip} size="sm" placement="top">
        {chip}
      </Tooltip>
    );
  }

  return chip;
};